/**
 * 应用 CGI 调用接口封装
 * create by sevencai@20210910
 * contact seven.cailidan@gmail.com
 */
import FissionProc from "@tencent/midas-cgi/cgis/fission_proc"
import { getCgiObj } from "./cgi"
import { getNowTimeStamp } from "../utils/helper"

const defaultConfig = {
  hideLoading: false
}

class FissionApi {
  private cgiObj
  private cgiNames
  /**
   * 构造函数
   * @param options 初始化cgiObj所需参数
   */
  constructor(options: { [index: string]: any }) {
    const { pf, sandbox, sessionObj, offerId } = options

    this.cgiObj = getCgiObj({
      pf,
      sandbox,
      sessionObj,
      offerId
    })

    this.cgiNames = {
      fissionProc: new FissionProc(defaultConfig)
    }
  }

  /**
   * 查询裂变信息
   * @param groupId
   */
  queryFissionActivity({ groupId }): Promise<any> {
    const params = {
      action: "query_activity",
      drm_info: encodeURIComponent(`version=3.0&group_id=${groupId}`)
    }

    return this.cgiObj.request(this.cgiNames["fissionProc"], params)
  }

  /**
   * 发起裂变, 获得裂变id
   * @param gameid
   */
  presentFissionCoupon({ activityId }): Promise<any> {
    const params = {
      action: "birthday_present",
      activity_id: activityId,
      // change_openid: 1,
      accounttype: "qb"
    }

    return this.cgiObj.request(this.cgiNames["followActivity"], params)
  }

  /**
   * 查询用户裂变券信息
   * @param groupId
   */
  queryUserFissionCoupons({ groupId }): Promise<any> {
    const params = {
      action: "query_tickets",
      drm_info: encodeURIComponent(`version=3.0&group_id=${groupId}`)
    }

    return this.cgiObj.request(this.cgiNames["fissionProc"], params)
  }

  /**
   * 发起裂变
   * @param code_id
   * @param groupId
   */
  fission({ codeId, groupId }): Promise<any> {
    const params = {
      action: "create_fission",
      drm_info: encodeURIComponent(`version=3.0&group_id=${groupId}`),
      code_id: codeId
    }

    return this.cgiObj.request(this.cgiNames["fissionProc"], params)
  }

  /**
   * 查询我的裂变
   * @param groupId
   */
  queryUserFissions({ groupId }): Promise<any> {
    const params = {
      action: "query_my_fissions",
      drm_info: encodeURIComponent(`version=3.0&group_id=${groupId}`)
    }

    return this.cgiObj.request(this.cgiNames["fissionProc"], params)
  }

  /**
   * 查询裂变详情
   * @param fissionId
   * @param groupId
   */
  queryFissionDetail({ groupId, fissionId }): Promise<any> {
    const params = {
      action: "query_fission",
      drm_info: encodeURIComponent(`version=3.0&group_id=${groupId}`),
      fission_id: fissionId
    }

    return this.cgiObj.request(this.cgiNames["fissionProc"], params)
  }

  /**
   * 获得裂变的奖品
   * @param groupId
   * @param fissionId
   * @param gameId
   * @param gamePlatformId
   */
  getFissionPresent({ groupId, fissionId, zoneid, gamePlatformId }): Promise<any> {
    const params = {
      action: "fission_present",
      drm_info: encodeURIComponent(`version=3.0&group_id=${groupId}`),
      fission_id: fissionId,
      zoneid
    }

    return this.cgiObj.request(this.cgiNames["fissionProc"], params)
  }
}

/**
 * 发起裂变
 * @param mainCgi
 * @param fissionGroupId
 * 封装好整个裂变流程，方便外部调用
 * 1、查询裂变活动信息，报错如1414活动过期，需要返回前端
 * 2、查询用户的券信息，券如果没查到说明出问题（备注如果是限次一次的，可以考虑先去查用户裂变信息，没有再查券）
 * 3、用券发起裂变
 */
const fission = async (mainCgi: { [index: string]: any }, fissionGroupId: string): Promise<any> => {
  // 查询用户活动信息
  const fissionActivityRes = await mainCgi.queryFissionActivity({
    groupId: fissionGroupId
  })

  if (fissionActivityRes.ret !== 0) {
    return fissionActivityRes
  }

  // 查用户的券信息
  const couponRes = await mainCgi.queryUserFissionCoupons({
    groupId: fissionGroupId
  })

  if (couponRes.ret !== 0) {
    return couponRes
  }

  // 获取 codeId
  let codeId = ""
  if (couponRes.info.tickets.length) {
    const filtered = couponRes.info.tickets.filter(ticket => {
      return ticket.expire_time * 1000 > new Date(getNowTimeStamp()).getTime()
    })

    if (filtered.length) {
      codeId = filtered[0].code_id
    }
  }

  // codeId 获取不到，需要告诉用户系统出现了问题，引导用户重新查看
  if (!codeId) {
    return {
      ret: -1,
      err_code: "-1",
      msg: "未查询到可用裂变券"
    }
  }

  // 获得裂变券后，发起裂变
  return await mainCgi.fission({
    groupId: fissionGroupId,
    codeId
  })
}

export { FissionApi, fission }
